import axios from "axios";
import { reportError } from "./errors";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Convert FastAPI's Pydantic validation error array
 *   detail: [{type, loc, msg, input, ctx, url}, ...]
 * into a single human-readable string. If `detail` is already a string we
 * pass it through; if it's anything else we coerce via JSON for safety.
 *
 * This runs in the response interceptor BEFORE the error propagates to
 * call sites, so every component (AuthGate, ChauffeurApp, AdminDashboard,
 * etc.) receives a plain string in `error.response.data.detail` and is
 * safe to pass to `toast.error()` / React directly.
 */
function flattenDetail(detail) {
  if (typeof detail === "string") return detail;
  if (!Array.isArray(detail)) {
    if (detail && typeof detail === "object" && typeof detail.msg === "string") {
      return detail.msg;
    }
    try { return JSON.stringify(detail); } catch { return "Request failed"; }
  }
  // Pydantic validation error array — surface the first message, prefixed
  // by the offending field for clarity ("password: must be at least 8 chars").
  const first = detail[0];
  if (!first) return "Validation failed";
  const field = Array.isArray(first.loc) ? first.loc.filter((p) => p !== "body").join(".") : "";
  const msg = first.msg || "invalid";
  return field ? `${field}: ${msg}` : msg;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("auth_token");
    }
    if (error.response?.data?.detail !== undefined) {
      error.response.data.detail = flattenDetail(error.response.data.detail);
    }
    // Auto-report server crashes & network failures to the admin inbox.
    // 4xx (client errors like validation, 401, 403, 404, 409) are NOT
    // reported — they're expected and would just be noise.
    try {
      const status = error.response?.status;
      const isServerError = status >= 500 && status < 600;
      const isNetworkFailure = !error.response && (
        error.code === "ERR_NETWORK"
        || error.code === "ECONNABORTED"
        || error.code === "ETIMEDOUT"
      );
      if (isServerError || isNetworkFailure) {
        reportError(error, {
          severity: isServerError && status >= 502 ? "critical" : "error",
          component: "axios",
          extra: {
            method: error.config?.method,
            url: error.config?.url,
            status: status || null,
            code: error.code || null,
          },
        });
      }
    } catch {
      /* never break callers over a logging failure */
    }
    return Promise.reject(error);
  }
);

export default api;
export { API_BASE };
